import React, { useState, useImperativeHandle, forwardRef, useEffect, useCallback, useRef, useMemo } from 'react';
import { Box, CircularProgress, InputAdornment, IconButton } from '@mui/material';
import { AttachFile as AttachFileIcon, Clear as ClearIcon } from '@mui/icons-material';
import FormContainer, { FormContainerRef } from '@/components/form/FormContainer.tsx';
import { useTranslation } from 'react-i18next';
import { useNotifications } from '@core/hooks/useNotifications';
import FormSelect from '@/components/form/FormSelect.tsx';
import FormTextField from '@/components/form/FormTextField.tsx';
import { v4 as uuidv4 } from 'uuid';
import { eventBus, EventTypes } from '@core/pubsub/EventBus.ts';
import { RfidTagFileImporterRef } from './types.ts';
import { useApiStore } from '@core/store';
import { WcnButton } from '@/components';

interface RfidTagFileImporterProps {
    onImportRfidTagFile: (data: any) => void;
    onCancelImporterEditor: () => void;
    onAddProduct?: (product: any) => void;
}

const RfidTagFileImporter = forwardRef<RfidTagFileImporterRef, RfidTagFileImporterProps>(
    ({ onImportRfidTagFile, onCancelImporterEditor, onAddProduct }, ref) => {
        const { t } = useTranslation();
        const { notifyErrorResponse } = useNotifications();
        const [loading, setLoading] = useState(false);
        const { rfidTagProducts, loadRfidTagProducts } = useApiStore();
        const [productTypeId, setProductTypeId] = useState<string>('');
        const [selectedFile, setSelectedFile] = useState<File | null>(null);
        const formRef = useRef<FormContainerRef>(null);
        const [formValid, setFormValid] = useState(false);
        const [fileInputKey, setFileInputKey] = useState(0);
        const fileInputRef = useRef<HTMLInputElement>(null);

        const load = useCallback(async () => {
            setLoading(true);
            try {
                await loadRfidTagProducts();
            } catch (error) {
                notifyErrorResponse(error);
            } finally {
                setLoading(false);
            }
        }, []);

        const resetValidation = useCallback(() => {
            setProductTypeId('');
            setSelectedFile(null);
            setFileInputKey((prev) => prev + 1);
        }, []);

        useImperativeHandle(
            ref,
            () => ({
                load,
                resetValidation
            }),
            [load, resetValidation]
        );

        useEffect(() => {
            const unsubscribeReload = eventBus.on(EventTypes.WIDGETS_RELOAD, load);
            load();

            return () => {
                unsubscribeReload();
            };
        }, [load]);

        const handleFileChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
            const file = event.target.files?.[0];
            if (file) setSelectedFile(file);
        }, []);

        const handleClearFile = useCallback(() => {
            setSelectedFile(null);
            setFileInputKey((prev) => prev + 1);
        }, []);

        const handleFileInputClick = useCallback(() => {
            fileInputRef.current?.click();
        }, []);

        const handleImportRfidTags = useCallback(() => {
            if (!selectedFile || !productTypeId) {
                return;
            }
            onImportRfidTagFile({
                rfidTagProducts,
                productTypeId,
                selectedFile
            });
        }, [selectedFile, productTypeId, onImportRfidTagFile, rfidTagProducts]);

        const handleSelectAddProduct = useCallback(
            (e?: React.MouseEvent) => {
                e?.preventDefault();
                e?.stopPropagation();

                const newProduct: any = {
                    id: uuidv4(),
                    articleNumber: null,
                    name: null,
                    productTypeId: null,
                    disabled: false
                };

                setProductTypeId('');
                setSelectedFile(null);

                if (onAddProduct) {
                    onAddProduct(newProduct);
                }
            },
            [onAddProduct]
        );

        const productItems = useMemo(
            () => [{ id: '__add__', name: `${t('add')}...` }, ...rfidTagProducts],
            [rfidTagProducts]
        );

        return (
            <FormContainer formRef={formRef} value={formValid} onChange={setFormValid}>
                <Box>
                    {loading ? (
                        <Box display="flex" justifyContent="center" p={3}>
                            <CircularProgress />
                        </Box>
                    ) : (
                        <>
                            <input
                                key={fileInputKey}
                                ref={fileInputRef}
                                type="file"
                                accept=".csv,.txt"
                                onChange={handleFileChange}
                                style={{ display: 'none' }}
                            />
                            <FormTextField
                                name="selectedFile"
                                fullWidth
                                label={t('selectFile')}
                                value={selectedFile?.name || ''}
                                onClick={() => {
                                    handleFileInputClick();
                                }}
                                sx={{
                                    mb: 2
                                }}
                                InputProps={{
                                    readOnly: true,
                                    startAdornment: (
                                        <InputAdornment position="start" sx={{ mr: 1, mb: 0.5 }}>
                                            <AttachFileIcon />
                                        </InputAdornment>
                                    ),
                                    endAdornment: selectedFile && (
                                        <InputAdornment position="end">
                                            <IconButton
                                                size="small"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    handleClearFile();
                                                }}
                                                sx={{
                                                    mr: 1
                                                }}
                                            >
                                                <ClearIcon fontSize="small" />
                                            </IconButton>
                                        </InputAdornment>
                                    )
                                }}
                            />

                            <FormSelect
                                name="productTypeId"
                                label={t('productType')}
                                value={productTypeId}
                                onChange={(v) => {
                                    if (v === '__add__') {
                                        handleSelectAddProduct();
                                        return;
                                    }
                                    setProductTypeId(v);
                                }}
                                items={productItems}
                                placeholder={t('selectAType')}
                                itemText="name"
                                itemValue="id"
                                sx={{ mb: 2, width: '100%' }}
                            />
                            <WcnButton
                                variant="primary"
                                disabled={!(selectedFile && productTypeId)}
                                onClick={handleImportRfidTags}
                                style={{ width: '100%' }}
                            >
                                {t('importRfidTags')}
                            </WcnButton>
                        </>
                    )}
                </Box>
            </FormContainer>
        );
    }
);

RfidTagFileImporter.displayName = 'RfidTagFileImporter';

export default RfidTagFileImporter;
